// src/components/OverViewsPage/OverviewTabsChart/OverviewTabsChart.jsx
import React, { useMemo, useState, useEffect } from "react";
import "./OverviewTabsChart.css";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import useOverviewSeries from "@/Profile/hooks/useOverviewSeries";

// ✅ Patient info (doctor view)
import { fetchUserById } from "../../../../Profile/redux/thunks/userThunks";

// ✅ Tabs keys ثابتة (لازم تبقى كما هي لأن hook يعتمد عليها)
const tabsConfig = [
  { key: "bloodSugar", i18nKey: "overviewTabs.tabs.bloodSugar" },
  { key: "HeartRate", i18nKey: "overviewTabs.tabs.heartRate" },
  { key: "bloodPressure", i18nKey: "overviewTabs.tabs.bloodPressure" },
  { key: "Weight", i18nKey: "overviewTabs.tabs.weight" },
];

// ✅ Filtres temps — NE PAS RENOMMER
const timeFilters = ["ALL", "1M", "6M", "1Y"];

/** Courbe lissée (points: number[] ou (number|null)[]) */
const makeSmoothPath = (getX, getY, arr) => {
  const pts = arr.map((v, i) => ({ v, i })).filter((p) => typeof p.v === "number");
  if (pts.length < 2) return "";
  let d = `M ${getX(pts[0].i)} ${getY(pts[0].v)}`;
  for (let k = 1; k < pts.length; k++) {
    const i1 = pts[k - 1].i,
      y1 = getY(pts[k - 1].v);
    const i2 = pts[k].i,
      y2 = getY(pts[k].v);
    const x1 = getX(i1),
      x2 = getX(i2);
    const cp1x = x1 + (x2 - x1) * 0.4;
    const cp1y = y1;
    const cp2x = x2 - (x2 - x1) * 0.4;
    const cp2y = y2;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x2} ${y2}`;
  }
  return d;
};

const Chart = ({ tab, timeKey, getSeries, normalZone }) => {
  const { t } = useTranslation();

  const mainColor = "#006eea ";
  const diaColor = "#6B7280"; // gris pour DIA

  const { labels, min, max, count, points, sys, dia } = useMemo(() => {
    const s = getSeries(tab.key, timeKey);

    if (tab.key === "bloodPressure") {
      return {
        labels: s.labels,
        min: s.min,
        max: s.max,
        count: s.count,
        sys: s.sys,
        dia: s.dia,
      };
    }

    return {
      labels: s.labels,
      min: s.min,
      max: s.max,
      count: s.count,
      points: s.points,
    };
  }, [tab, timeKey, getSeries]);

  // dimensions
  const width = 800;
  const height = 260;
  const margin = { top: 20, right: 40, bottom: 40, left: 60 };
  const chartW = width - margin.left - margin.right;
  const chartH = height - margin.top - margin.bottom;

  // protection min/max
  const yMin = Number.isFinite(min) ? min : 0;
  const yMax = Number.isFinite(max) && max !== min ? max : yMin + 1;

  const getX = (i) => {
    const denom = Math.max(1, labels.length - 1);
    return margin.left + (i * chartW) / denom;
  };
  const getY = (v) => margin.top + ((yMax - v) / (yMax - yMin)) * chartH;

  // grille Y
  const gridCount = 4;
  const gridStep = (yMax - yMin) / gridCount || 1;
  const gridLines = Array.from({ length: gridCount + 1 }, (_, i) =>
    Math.round((yMin + i * gridStep) * 100) / 100
  );

  // ticks X (jusqu'à 6 labels espacés)
  const xticks = useMemo(() => {
    const n = labels.length;
    if (!n) return [];
    const maxTicks = Math.min(6, n);
    const step = Math.max(1, Math.floor(n / maxTicks));
    const ticks = [];
    for (let i = 0; i < n; i += step) ticks.push({ i, text: labels[i] });
    if (ticks[ticks.length - 1]?.i !== n - 1) {
      ticks.push({ i: n - 1, text: labels[n - 1] });
    }
    return ticks;
  }, [labels]);

  // Tooltip
  const [tip, setTip] = useState({ visible: false, x: 0, y: 0, lines: [], idx: -1, date: "" });
  const showTip = (idx, ev, lines) => {
    const svg = ev.currentTarget.closest("svg");
    const wrap = ev.currentTarget.closest(".otc-canvas");
    if (!svg || !wrap) return;

    const srect = svg.getBoundingClientRect();
    const wrect = wrap.getBoundingClientRect();
    const x = (getX(idx) / width) * srect.width + (srect.left - wrect.left);

    const ys = lines
      .map((l) => (typeof l.value === "number" ? getY(l.value) : null))
      .filter((v) => typeof v === "number");
    const y = ys.reduce((a, b) => a + b, 0) / (ys.length || 1) + (srect.top - wrect.top);

    setTip({
      visible: true,
      x,
      y,
      lines,
      idx,
      date: labels[idx] || "",
    });
  };
  const hideTip = () => setTip((tt) => ({ ...tt, visible: false }));

  // paths
  const pathSingle = points ? makeSmoothPath(getX, getY, points) : "";
  const pathSYS = sys ? makeSmoothPath(getX, getY, sys) : "";
  const pathDIA = dia ? makeSmoothPath(getX, getY, dia) : "";

  const nz = normalZone?.[tab.key];

  return (
    <div
      className="otc-canvas"
      role="img"
      aria-label={t("overviewTabs.chartAria")}
      style={{ position: "relative" }}
    >
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        {/* Grille Y + labels */}
        {gridLines.map((val) => (
          <g key={`gy-${val}`}>
            <line
              x1={margin.left}
              x2={width - margin.right}
              y1={getY(val)}
              y2={getY(val)}
              stroke="#e2e8f0"
              strokeWidth="1"
            />
            <text
              x={margin.left - 10}
              y={getY(val) + 4}
              textAnchor="end"
              fontSize="12"
              fill="#94a3b8"
            >
              {val}
            </text>
          </g>
        ))}

        {/* Zone normale (pour mono-séries seulement) */}
        {nz && tab.key !== "bloodPressure" && (
          <rect
            x={margin.left}
            y={getY(nz.max)}
            width={chartW}
            height={getY(nz.min) - getY(nz.max)}
            fill="#22c55e"
            fillOpacity="0.08"
          />
        )}

        {/* Courbes */}
        {tab.key !== "bloodPressure" ? (
          <>
            <path
              d={pathSingle}
              stroke={mainColor}
              strokeWidth="3"
              fill="none"
              strokeDasharray="8 6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {points?.map((v, i) =>
              typeof v === "number" ? (
                <g key={`p-${i}`}>
                  <circle
                    cx={getX(i)}
                    cy={getY(v)}
                    r="10"
                    fill="transparent"
                    onMouseEnter={(e) =>
                      showTip(i, e, [{ label: tab.label, value: v, color: mainColor }])
                    }
                    onMouseLeave={hideTip}
                    style={{ cursor: "pointer" }}
                  />
                  <circle
                    cx={getX(i)}
                    cy={getY(v)}
                    r="4"
                    fill={mainColor}
                    stroke="white"
                    strokeWidth="2"
                    style={{
                      filter: "drop-shadow(0 2px 6px rgba(47,117,148,.4))",
                      pointerEvents: "none",
                    }}
                  />
                </g>
              ) : null
            )}
          </>
        ) : (
          <>
            {/* SYS */}
            <path
              d={pathSYS}
              stroke={mainColor}
              strokeWidth="3"
              fill="none"
              strokeDasharray="8 6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* DIA */}
            <path
              d={pathDIA}
              stroke={diaColor}
              strokeWidth="3"
              fill="none"
              strokeDasharray="8 6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Points interactifs (SYS & DIA) */}
            {labels.map((_, i) => {
              const hasSYS = typeof sys?.[i] === "number";
              const hasDIA = typeof dia?.[i] === "number";
              if (!hasSYS && !hasDIA) return null;

              const lines = [];
              if (hasSYS) lines.push({ label: t("overviewTabs.bp.sys"), value: sys[i], color: mainColor });
              if (hasDIA) lines.push({ label: t("overviewTabs.bp.dia"), value: dia[i], color: diaColor });

              return (
                <g key={`bp-${i}`}>
                  <circle
                    cx={getX(i)}
                    cy={getY(hasSYS ? sys[i] : hasDIA ? dia[i] : yMin)}
                    r="12"
                    fill="transparent"
                    onMouseEnter={(e) => showTip(i, e, lines)}
                    onMouseLeave={hideTip}
                    style={{ cursor: "pointer" }}
                  />
                  {hasSYS && (
                    <circle
                      cx={getX(i)}
                      cy={getY(sys[i])}
                      r="4"
                      fill={mainColor}
                      stroke="white"
                      strokeWidth="2"
                      style={{
                        filter: "drop-shadow(0 2px 6px rgba(47,117,148,.4))",
                        pointerEvents: "none",
                      }}
                    />
                  )}
                  {hasDIA && (
                    <circle
                      cx={getX(i)}
                      cy={getY(dia[i])}
                      r="4"
                      fill={diaColor}
                      stroke="white"
                      strokeWidth="2"
                      style={{
                        filter: "drop-shadow(0 2px 6px rgba(107,114,128,.4))",
                        pointerEvents: "none",
                      }}
                    />
                  )}
                </g>
              );
            })}
          </>
        )}

        {/* Axe X */}
        {xticks.map((tt) => (
          <text
            key={`xt-${tt.i}`}
            x={getX(tt.i)}
            y={height - 10}
            textAnchor="middle"
            fontSize="12"
            fill="#94a3b8"
          >
            {tt.text}
          </text>
        ))}
      </svg>

      {/* Tooltip */}
      {tip.visible && (
        <div
          className="health-tooltip"
          style={{
            position: "absolute",
            left: tip.x - 60,
            top: tip.y - 76,
            background: "rgba(247,248,249,1)",
            color: "#333",
            padding: "12px 16px",
            borderRadius: "8px",
            fontSize: "13px",
            fontFamily: "Poppins",
            pointerEvents: "none",
            zIndex: 1000,
            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
            minWidth: 140,
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontWeight: 600,
              marginBottom: 4,
              color: "#6B7280",
              fontSize: 11,
              textTransform: "uppercase",
            }}
          >
            {tab.label}
          </div>

          {tip.lines?.map((ln) => (
            <div
              key={ln.label}
              style={{
                fontWeight: 700,
                fontSize: 16,
                lineHeight: 1.2,
                color: ln.color || "#006eea ",
              }}
            >
              {ln.label ? `${ln.label}: ${ln.value}` : ""}
            </div>
          ))}

          <div style={{ fontSize: 11, marginTop: 6, color: "#64748b" }}>{tip.date}</div>

          <div
            style={{
              position: "absolute",
              bottom: "-6px",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: "6px solid rgba(247,248,249,1)",
            }}
          />
        </div>
      )}
    </div>
  );
};

/* ============================================================
   ✅ OverviewTabsChart (Doctor view)
   - utilise patientId (prop) au lieu de auth.user
   - charge le patient via fetchUserById pour afficher infos
   ============================================================ */
const OverviewTabsChart = ({ patientId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!patientId) return;
    dispatch(fetchUserById(patientId));
  }, [dispatch, patientId]);

  const patient =
    useSelector((s) => s.users?.selectedUser) ||
    useSelector((s) => s.users?.userById?.[patientId]) ||
    useSelector((s) => s.users?.currentUser) ||
    null;

  const patientLoading =
    useSelector((s) => s.users?.loadingSelectedUser) ??
    useSelector((s) => s.users?.loading) ??
    false;

  const targetUserId = patientId;

  const [activeTab, setActiveTab] = useState(0);
  const [activeFilter, setActiveFilter] = useState(0);

  const tab = useMemo(() => {
    const cfg = tabsConfig[activeTab];
    return {
      ...cfg,
      label: t(cfg.i18nKey),
    };
  }, [activeTab, t]);

  const timeKey = timeFilters[activeFilter];

  const { getSeries, normalZone } = useOverviewSeries(targetUserId);

  if (!patientId) return null;

  if (patientLoading && !patient) {
    return (
      <section className="otc">
        <div className="otc-head">
          <h3>{t("overviewTabs.title")}</h3>
        </div>
        <div style={{ padding: 16 }}>{t("overviewTabs.loadingPatient")}</div>
      </section>
    );
  }

  return (
    <section className="otc">
      <div className="otc-head">
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <h3 style={{ margin: 0 }}>{t("overviewTabs.title")}</h3>

          {patient && (
            <div style={{ fontSize: 12, color: "#64748b" }}>
              {t("overviewTabs.patientLabel")}{" "}
              {patient.fullName ||
                patient.name ||
                `${patient.firstName || ""} ${patient.lastName || ""}`.trim() ||
                patient.email ||
                patientId}
            </div>
          )}
        </div>

        <div className="otc-filters">
          {timeFilters.map((f, i) => (
            <button
              key={f}
              className={i === activeFilter ? "active" : ""}
              onClick={() => setActiveFilter(i)}
              type="button"
            >
              {t(`overviewTabs.filters.${f}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="otc-tabs">
        {tabsConfig.map((cfg, i) => (
          <button
            key={cfg.key}
            className={i === activeTab ? "active" : ""}
            onClick={() => setActiveTab(i)}
            type="button"
          >
            {t(cfg.i18nKey)}
          </button>
        ))}
      </div>

      <Chart tab={tab} timeKey={timeKey} getSeries={getSeries} normalZone={normalZone} />
    </section>
  );
};

export default OverviewTabsChart;
